import { useLayoutContext } from '@/container/context/layout/layout-context';
import { SidebarMembersProps } from '@modules/members/members.types';
import { Menu, Skeleton, Tooltip } from 'antd';
import { MenuItemType } from 'antd/es/menu/interface';
import { MemberMobileCard } from './member-mobile-card';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { FaChevronRight } from 'react-icons/fa6';
import { capitalize } from '@shared/utils/capitalize';
import { useTranslation } from 'react-i18next';
import { GroupAvatar } from '@modules/conversations/features/all-members-conversation/components/group-avatar';
import {
  ALL_MEMBERS_KEY,
  ALL_MEMBERS_ROUTE,
} from '@modules/conversations/features/all-members-conversation/all-members-conversation.constants';

const style: MenuItemType['style'] = { height: 50, padding: 0, textAlign: 'center', lineHeight: 2.8 };

export function MobileMembersView(props: SidebarMembersProps) {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    workspaceMembers: { data, initialLoading, hasNextPage },
    layoutContext,
  } = props;
  const { closeSidebarMobileDrawer } = layoutContext;
  const { isSubmenuOverlay, setIsSubmenuOverlay } = useLayoutContext();
  const filteredMembers = data?.data ?? [];
  const selectedKey = pathname.startsWith(ALL_MEMBERS_ROUTE) ? ALL_MEMBERS_KEY : memberId;

  function navigateToMemberChat(memberId: number) {
    closeSidebarMobileDrawer();
    navigate({
      pathname: '/conversations/member/' + memberId,
    });
  }

  const items: MenuItemType[] = [
    {
      key: ALL_MEMBERS_KEY,
      title: '',
      label: (
        <span className="grid place-items-center">
          <GroupAvatar size={36} isOnline={filteredMembers.some((m) => m?.userOnlineManagement?.isActive)} />
        </span>
      ),
      onClick: () => {
        closeSidebarMobileDrawer();
        navigate({ pathname: ALL_MEMBERS_ROUTE });
      },
    },
    ...(filteredMembers?.map((member) => ({
      key: member?.id + '',
      title: '',
      label: <MemberMobileCard {...member} />,
      onClick: () => navigateToMemberChat(Number(member?.id)),
    })) ?? []),
  ].map(({ ...item }) => ({
    ...item,
    style,
  }));
  const { t } = useTranslation();

  return (
    <div className={clsx('flex flex-col items-center justify-center mt-5', hasNextPage ? 'mb-5' : 'mb-2')}>
      <p className="text-text-600 text-xs uppercase!">{t('member_other')}</p>
      {initialLoading ? (
        <Skeleton active paragraph={false} />
      ) : (
        <>
          <Menu
            className="mobile-custom-menu"
            style={{ border: 0, width: '100%' }}
            items={items}
            selectedKeys={selectedKey ? [selectedKey] : undefined}
          />
          {hasNextPage && (
            <Tooltip title={capitalize(t('view_all'))}>
              <div
                id="view-all"
                className={clsx(
                  'hover:bg-primary-50 text-text-400 rounded-full items-center group/view cursor-pointer',
                  'px-2 py-1 border border-text-100 mt-2 gap-2',
                  isSubmenuOverlay === 'members' ? 'bg-primary-100' : ''
                )}
                onClick={() => setIsSubmenuOverlay((prev) => (prev === 'members' ? null : 'members'))}
              >
                <FaChevronRight
                  size={10}
                  style={{ transform: isSubmenuOverlay === 'members' ? 'rotate(180deg)' : '' }}
                />
              </div>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
}
